import { DataType, FormDialogMode, SelectOption, useNotify } from '@globaleris/ui.library'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumTipoPeaje } from '../../../api'
import { ProfileModel } from '../../../api/Argentia/endpoints/profiles/model/profiles'
import { EnumNivelDesglose } from '../../../api/Argentia/enums/enumNivelDesglose'
import { EnumTipoPerfil } from '../../../api/oData/enums/enumTipoPerfil'
import { EnumSubtipoPerfil } from '../../../api/oData/enums/enumSubtipoPerfil'
import { enumToSelectOptionArray } from '../../../helpers'
import { debounce } from '../../../helpers/debounceHelper'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks'
import { fetchTolls } from '../../contracts/contracts.service'
import { mapFetchTollsToSelectOptions } from '../profiles.mapper'
import { fetchIsCodeDuplicated } from '../profiles.services'

export interface ProfileFormData {
  id?: number
  code: string
  description: string
  profileType: EnumTipoPerfil
  profileSubtype: EnumSubtipoPerfil
  commodity: EnumCommodity
  breakdownLevel: EnumNivelDesglose
  toll: EnumTipoPeaje
}

interface ProfilesFormDialogProps {
  formMode: FormDialogMode
  open: boolean
  onClose: (formData: any) => void
  checkCodeLocally?: (code: string, profileID?: number) => boolean
  inputData?: ProfileModel
  commodity: EnumCommodity
  profileType: EnumTipoPerfil
}

export const ProfilesFormDialog: React.FC<ProfilesFormDialogProps> = ({
  formMode,
  open,
  onClose,
  checkCodeLocally,
  inputData,
  commodity,
  profileType
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'profiles'])
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const { info, error } = useNotify()
  const [tollOptions, setTollSelectOptions] = useState<SelectOption[]>([])

  const [codeExists, setCodeExists] = useState<boolean>(false)
  const [isFetchingOptions, setIsFetchingOptions] = useState<boolean>(false)
  // #region Handlers Starts
  const onFormCloseHandler = async (formData: ProfileFormData) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(formDefinition?.sections!, formData)) {
        error(t('profiles:components.profilesFormDialog.notifications.incompleteForm'))
        return
      }
    }
    closeForm()
    setCodeExists(false)
    setTollSelectOptions([])
    onClose(formData)
  }
  // #endregion Handlers Ends

  // #region Functions Starts

  // Check if code exists in profiles data or via API
  const checkCodeExists = async (code: string, profileID: number | undefined) => {
    if (!code || code.trim() === '') {
      setCodeExists(false)
      return
    }

    setFormWaiting(true)

    // First check locally in profilesData
    const existsLocally = checkCodeLocally && checkCodeLocally(code, inputData?.id)

    if (existsLocally) {
      setCodeExists(true)
      setFormWaiting(false)

      return
    }

    // If not found locally, check via API
    await fetchIsCodeDuplicated(code, profileID)
      .then(response => {
        if (response.success && response.data !== undefined) {
          setCodeExists(response.data)
        } else {
          error(t('profiles:fetchErrors.codeIsDuplicated'))
        }
      })
      .finally(() => {
        setFormWaiting(false)
      })
  }

  const debouncedCheckCodeExists = useCallback(
    debounce((code: string, profileID?: number) => {
      checkCodeExists(code, profileID)
    }, 750),
    [inputData]
  )

  // #endregion Functions Ends

  // #region Form Definition Starts
  const formDefinition = React.useMemo<FormDialogDefinitionProps>(
    () => ({
      id: 'ADD-EDIT',
      sections: [
        {
          inputs: [
            {
              name: 'id',
              display: false
            },
            {
              name: 'code',
              label: t('profiles:components.profilesFormDialog.inputs.code'),
              type: DataType.TEXT,
              required: true,
              helpText: t('profiles:components.profilesFormDialog.helpText.code'),
              errorState: codeExists,
              errorText: codeExists
                ? t('profiles:components.profilesFormDialog.errorText.codeExists')
                : '',
              onChange: (value: string | number, formData: any) => {
                const profileCode = value.toString()
                const profileID = formData.id
                debouncedCheckCodeExists(profileCode, profileID)
              }
            },
            {
              name: 'description',
              label: t('profiles:components.profilesFormDialog.inputs.description'),
              type: DataType.TEXT
            },
            {
              name: 'profileType',
              label: t('profiles:components.profilesFormDialog.inputs.profileType'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumTipoPerfil, 'enums:EnumTipoPerfil'),
              readOnly: true
            },
            {
              name: 'profileSubtype',
              label: t('profiles:components.profilesFormDialog.inputs.profileSubtype'),
              type: DataType.SELECT,
              selectOptions:
                formMode === FormDialogMode.EDIT
                  ? enumToSelectOptionArray(EnumSubtipoPerfil, 'enums:EnumSubtipoPerfil')
                  : enumToSelectOptionArray(EnumSubtipoPerfil, 'enums:EnumSubtipoPerfil').filter(
                      opt => opt.value !== EnumSubtipoPerfil.Historico
                    ),
              required: true
            },
            {
              name: 'commodity',
              label: t('profiles:components.profilesFormDialog.inputs.commodity'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
              readOnly: true
            },
            {
              name: 'breakdownLevel',
              label: t('profiles:components.profilesFormDialog.inputs.breakdownLevel'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumNivelDesglose, 'enums:EnumNivelDesglose'),
              required: true
            },
            {
              name: 'toll',
              label: t('profiles:components.profilesFormDialog.inputs.toll'),
              type: DataType.SELECT,
              selectOptions: tollOptions,
              isLoading: isFetchingOptions
            }
          ]
        }
      ]
    }),
    [codeExists, tollOptions, isFetchingOptions, formMode]
  )
  // #endregion Form Definition Ends

  // #region Hooks Starts
  useEffect(() => {
    if (open) {
      getTollOptions(commodity)
      openForm({
        definition: formDefinition,
        title:
          formMode === FormDialogMode.EDIT
            ? t('profiles:components.profilesFormDialog.edit.title')
            : t('profiles:components.profilesFormDialog.add.title'),
        mode: formMode,
        isWaiting: false,
        ...(formMode === FormDialogMode.EDIT && { inputData: { ...inputData } }),
        ...(formMode === FormDialogMode.ADD && {
          inputData: { commodity, profileType, profileSubtype: EnumSubtipoPerfil.Estandar }
        }),
        onClose: onFormCloseHandler
      })
    }
  }, [open])

  useEffect(() => {
    updateFormDefinition(formDefinition)
  }, [formDefinition])

  const getTollOptions = useCallback(
    async commodity => {
      setIsFetchingOptions(true)
      await fetchTolls(commodity)
        .then(response => {
          if (response.success) {
            setTollSelectOptions(mapFetchTollsToSelectOptions(response.data))
          } else {
            error(t('profiles:fetchErrors.tollOptions'))
          }
        })
        .finally(() => {
          setIsFetchingOptions(true)
          setIsFetchingOptions(false)
        })
    },
    [commodity]
  )
  // #endregion Hooks Ends

  return (
    <>
      <CustomFormDialog {...formProps} />
    </>
  )
}
